/**
 * Notification mapper — shared normalize + grouping layer.
 *
 * Used by both polling (current) and WebSocket (future) data sources.
 * Neither the hook nor the UI should know which transport produced the event.
 *
 * Responsibilities:
 *   normalizeEvent()  — raw polling diff / WS payload → NotificationItem
 *   pushNotification() — immutable prepend with MAX_HISTORY cap
 *   groupByDate()     — bucket items into "Hom nay" / "Hom qua" / "Truoc do"
 *   relativeTime()    — "5 phut truoc", "2 gio truoc", etc.
 *   persistItems() / loadItems() — sessionStorage per idw
 */

import { getNotificationType, SEVERITY } from "../constants/notificationTypes";
import { cacheGet, cacheSet, cacheRemove } from "./cache";

/* ─── Config ─── */
const MAX_HISTORY = 50;
const STORAGE_PREFIX = "notif_";
const STORAGE_TTL = 24 * 60 * 60 * 1000; // 24h

/* ─── Helpers ─── */

function interpolate(tpl, vars) {
  return tpl.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? "");
}

/**
 * Relative time string (Vietnamese).
 * @param {string|number|Date} timestamp
 * @returns {string}
 */
export function relativeTime(timestamp) {
  const now = Date.now();
  const then = typeof timestamp === "number" ? timestamp : new Date(timestamp).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "Vừa xong";
  if (mins < 60) return `${mins} phút trước`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} giờ trước`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Hôm qua";
  if (days < 7) return `${days} ngày trước`;
  // Fallback: dd/mm HH:mm
  const d = new Date(then);
  return (
    String(d.getDate()).padStart(2, "0") +
    "/" +
    String(d.getMonth() + 1).padStart(2, "0") +
    " " +
    String(d.getHours()).padStart(2, "0") +
    ":" +
    String(d.getMinutes()).padStart(2, "0")
  );
}

/* ─── Core: normalize ─── */

/**
 * Convert a raw event into a NotificationItem.
 *
 * Works for BOTH data sources:
 *   - Polling: { type: "NEW_ORDER", count: 3 }
 *   - WebSocket (future): { type: "ORDER_CANCELLED", order_id: "...", channel: "shopee" }
 *
 * @param {object} raw - Raw event from any data source
 * @returns {object} NotificationItem
 */
export function normalizeEvent(raw) {
  const typeDef = getNotificationType(raw.type);
  const count = raw.count ?? 1;
  const now = new Date();

  return {
    id: raw.id || `${raw.type}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    type: typeDef.type,
    title: typeDef.title,
    description: interpolate(typeDef.descriptionTpl, { count }),
    created_at: raw.created_at || now.toISOString(),
    severity: typeDef.severity,
    meta: {
      order_id: raw.order_id || null,
      channel: raw.channel || null,
      count,
      ...(raw.meta || {}),
    },
  };
}

/**
 * Toast message text for a given raw event.
 */
export function toastText(raw) {
  const typeDef = getNotificationType(raw.type);
  const count = raw.count ?? 1;
  return interpolate(typeDef.toastTpl, { count });
}

/**
 * Toast severity → antd message method name.
 */
export function toastMethod(raw) {
  const typeDef = getNotificationType(raw.type);
  switch (typeDef.severity) {
    case SEVERITY.ERROR:
      return "error";
    case SEVERITY.WARNING:
      return "warning";
    default:
      return "info";
  }
}

/* ─── Core: push ─── */

/**
 * Immutably prepend a notification item, capping at MAX_HISTORY.
 * @param {Array} prev - Current items array
 * @param {object} item - NotificationItem to prepend
 * @returns {Array} New items array
 */
export function pushNotification(prev, item) {
  return [item, ...prev].slice(0, MAX_HISTORY);
}

/* ─── Core: group by date ─── */

/**
 * Bucket items into time groups for timeline rendering.
 *
 * Returns: [{ label: "Hom nay", items: [...] }, { label: "Hom qua", items: [...] }, ...]
 * Empty groups are omitted.
 */
export function groupByDate(items) {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 86_400_000;

  const buckets = {
    today: [],
    yesterday: [],
    older: [],
  };

  for (const item of items) {
    const ts = new Date(item.created_at).getTime();
    if (ts >= todayStart) {
      buckets.today.push(item);
    } else if (ts >= yesterdayStart) {
      buckets.yesterday.push(item);
    } else {
      buckets.older.push(item);
    }
  }

  const groups = [];
  if (buckets.today.length > 0) groups.push({ label: "Hôm nay", items: buckets.today });
  if (buckets.yesterday.length > 0) groups.push({ label: "Hôm qua", items: buckets.yesterday });
  if (buckets.older.length > 0) groups.push({ label: "Trước đó", items: buckets.older });
  return groups;
}

/* ─── Persistence: sessionStorage per idw ─── */

function storageKey(idw) {
  return `${STORAGE_PREFIX}${idw}`;
}

/**
 * Save notification items to sessionStorage for a given idw.
 */
export function persistItems(idw, items) {
  if (!idw) return;
  cacheSet(storageKey(idw), items, STORAGE_TTL);
}

/**
 * Load notification items from sessionStorage for a given idw.
 * Returns [] if nothing cached or expired.
 */
export function loadItems(idw) {
  if (!idw) return [];
  return cacheGet(storageKey(idw)) || [];
}

/**
 * Clear notification cache for a specific idw.
 */
export function clearItems(idw) {
  if (!idw) return;
  cacheRemove(storageKey(idw));
}
